/**
 * Página admin: gerenciamento dos pesos do scoring engine de recomendação.
 *
 * Mostra duas versões ATIVAS (uma por `name`: primary + exploratory) lado a
 * lado em um bar chart, lista o histórico de versões e oferece três ações:
 *   1. Recalcular agora (POST /retrain) — refaz aprendizado via regressão.
 *   2. Nova versão manual — form com sliders por feature (dinâmico).
 *   3. Ativar versão antiga — rollback de 1 clique.
 *
 * Auth: backend valida profile_slug=admin; chamadas de usuário comum
 * recebem 403. Aqui rendermos um warning se não-admin.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CheckCircle2,
  RefreshCcw,
  Plus,
  Sparkles,
  History,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  type FeatureInfo,
  type WeightsVersion,
  activateVersion,
  createVersion,
  listActive,
  listFeatures,
  listVersions,
  triggerRetrain,
} from "@/services/scoring-weights-api";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";

const RANKING_LABELS: Record<string, { name: string; descricao: string }> = {
  primary: {
    name: "Recomendados",
    descricao:
      "Lista padrão exibida nas vagas. Calibrada manualmente, robusta pra freelancers com pouco histórico.",
  },
  exploratory: {
    name: "Outras opções",
    descricao:
      "Lista alternativa em paralelo. Pesos aprendidos por regressão sobre o histórico de seleção humana.",
  },
};

const SOURCE_LABELS: Record<string, string> = {
  manual: "Manual",
  auto_regression: "Aprendido",
  seed: "Inicial",
};

const SOURCE_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  manual: "default",
  auto_regression: "secondary",
  seed: "outline",
};

export default function RecomendacaoPesos() {
  // Proteção da rota fica no ProtectedRoute (requiredScreen="recomendacao_pesos").
  // Backend valida o JWT independente — defesa em profundidade.
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [retrainOpen, setRetrainOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: features = [] } = useQuery({
    queryKey: ["scoring-weights", "features"],
    queryFn: listFeatures,
  });

  const { data: active = [], isLoading: loadingActive } = useQuery({
    queryKey: ["scoring-weights", "active"],
    queryFn: listActive,
  });

  const { data: history = [], isLoading: loadingHistory } = useQuery({
    queryKey: ["scoring-weights", "history"],
    queryFn: () => listVersions({ limit: 50 }),
  });

  const retrainMutation = useMutation({
    mutationFn: () => triggerRetrain(true),
    onSuccess: (res) => {
      toast({ title: "Recálculo concluído", description: `Nova versão ativada (${res.version_id.slice(0, 8)}).` });
      queryClient.invalidateQueries({ queryKey: ["scoring-weights"] });
      setRetrainOpen(false);
    },
    onError: (err) => {
      toast({
        title: "Falha ao recalcular",
        description: formatApiError(err).detail,
        variant: "destructive",
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => activateVersion(id),
    onSuccess: (res) => {
      toast({ title: "Versão ativada", description: `${res.name} agora usa a versão de ${format(new Date(res.created_at), "dd/MM HH:mm", { locale: ptBR })}.` });
      queryClient.invalidateQueries({ queryKey: ["scoring-weights"] });
    },
    onError: (err) => {
      toast({
        title: "Falha ao ativar",
        description: formatApiError(err).detail,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pesos das recomendações"
        subtitle="Cada peso decide o quanto um sinal (categoria, áreas, valor, etc.) conta no ranking. Existem duas listas em paralelo (recomendados e outras opções) e cada uma pode ser ajustada separadamente."
        actions={
          <>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Plus className="h-4 w-4" />
                Nova versão
              </Button>
            </DialogTrigger>
            <CreateVersionDialog
              features={features}
              activeVersions={active}
              onCreated={() => {
                queryClient.invalidateQueries({ queryKey: ["scoring-weights"] });
                setCreateOpen(false);
              }}
            />
          </Dialog>
          <Dialog open={retrainOpen} onOpenChange={setRetrainOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Sparkles className="h-4 w-4" />
                Recalcular agora
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Recalcular pesos automaticamente?</DialogTitle>
                <DialogDescription>
                  Vamos rodar o aprendizado sobre as últimas vagas e ativar a
                  nova versão da lista "Outras opções". A lista "Recomendados"
                  não é alterada por esse processo. Demora cerca de 1–2 minutos.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRetrainOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => retrainMutation.mutate()}
                  disabled={retrainMutation.isPending}
                  className="gap-1.5"
                >
                  {retrainMutation.isPending && <RefreshCcw className="h-4 w-4 animate-spin" />}
                  {retrainMutation.isPending ? "Recalculando..." : "Confirmar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      {/* Visualização gráfica das versões ativas */}
      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">Versão em uso agora</h2>
          <p className="text-sm text-muted-foreground">
            Comparação visual dos pesos ativos em cada lista.
          </p>
        </div>
        {loadingActive ? (
          <Skeleton className="h-72 w-full" />
        ) : active.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma versão ativa.</p>
        ) : (
          <ActiveWeightsChart active={active} />
        )}
      </section>

      {/* Histórico de versões */}
      <section className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <h2 className="text-base font-semibold">Histórico</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Versões anteriores. Pode ativar uma versão antiga a qualquer momento.
          </p>
        </div>
        <div className="rounded-md border">
          {loadingHistory ? (
            <div className="p-4">
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <HistoryTable
              versions={history}
              onActivate={(id) => activateMutation.mutate(id)}
              activatingId={activateMutation.isPending ? activateMutation.variables : null}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * Bar chart comparando os pesos das duas listas ativas (primary + exploratory).
 * Cada peso vira uma barra com duas séries lado a lado.
 */
function ActiveWeightsChart({ active }: { active: WeightsVersion[] }) {
  const byName = Object.fromEntries(active.map((v) => [v.name, v.weights]));
  const allKeys = new Set<string>();
  active.forEach((v) => Object.keys(v.weights).forEach((k) => allKeys.add(k)));

  // Filtra pesos onde TODAS as séries são zero pra não poluir o gráfico —
  // se um peso é zero em todas as versões ativas, ele não importa visualmente.
  const data = Array.from(allKeys)
    .map((k) => ({
      key: prettyFeatureLabel(k),
      primary: byName.primary?.[k] ?? 0,
      exploratory: byName.exploratory?.[k] ?? 0,
    }))
    .filter((row) => row.primary > 0 || row.exploratory > 0)
    .sort((a, b) => Math.max(b.primary, b.exploratory) - Math.max(a.primary, a.exploratory));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {active.map((v) => (
          <div key={v.id} className="rounded-md border p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm">
                {RANKING_LABELS[v.name]?.name ?? v.name}
              </span>
              <Badge variant={SOURCE_VARIANT[v.source]} className="text-[10px]">
                {SOURCE_LABELS[v.source] ?? v.source}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {RANKING_LABELS[v.name]?.descricao ?? "Lista paralela."}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Atualizada {formatDistanceToNow(new Date(v.created_at), { locale: ptBR, addSuffix: true })}
            </p>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={Math.max(280, data.length * 32)}>
        <BarChart data={data} layout="vertical" margin={{ left: 100, right: 24 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" domain={[0, 0.6]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
          <YAxis
            type="category"
            dataKey="key"
            tick={{ fontSize: 12 }}
            width={140}
          />
          <RechartsTooltip
            formatter={(value: number) => `${Math.round(value * 100)}%`}
            labelStyle={{ fontWeight: 500 }}
          />
          <Legend />
          <Bar
            dataKey="primary"
            name={RANKING_LABELS.primary?.name ?? "primary"}
            fill="#6366f1"
            radius={[0, 4, 4, 0]}
          />
          <Bar
            dataKey="exploratory"
            name={RANKING_LABELS.exploratory?.name ?? "exploratory"}
            fill="#f59e0b"
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function HistoryTable({
  versions,
  onActivate,
  activatingId,
}: {
  versions: WeightsVersion[];
  onActivate: (id: string) => void;
  activatingId: string | null;
}) {
  if (versions.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">Sem histórico ainda.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Lista</TableHead>
          <TableHead>Origem</TableHead>
          <TableHead>Criada</TableHead>
          <TableHead>Métricas</TableHead>
          <TableHead>Anotações</TableHead>
          <TableHead className="text-right">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((v) => (
          <TableRow key={v.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {RANKING_LABELS[v.name]?.name ?? v.name}
                </span>
                {v.is_active && (
                  <Badge variant="outline" className="text-[10px] gap-1 border-emerald-500/40 text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Em uso
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={SOURCE_VARIANT[v.source]} className="text-[10px]">
                {SOURCE_LABELS[v.source] ?? v.source}
              </Badge>
            </TableCell>
            <TableCell className="text-xs">
              {format(new Date(v.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
            </TableCell>
            <TableCell className="text-xs">
              {v.training_metrics ? (
                <Tooltip>
                  <TooltipTrigger className="underline decoration-dotted">
                    AUC {Number(v.training_metrics.auc_cv_mean ?? 0).toFixed(2)} ·{" "}
                    {String(v.training_metrics.n_pairs ?? "?")} pares
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs whitespace-pre-wrap text-xs">
                    {JSON.stringify(v.training_metrics, null, 2)}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
              {v.notes || <span className="opacity-50">sem anotação</span>}
            </TableCell>
            <TableCell className="text-right">
              {!v.is_active && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onActivate(v.id)}
                  disabled={activatingId === v.id}
                  className="h-7 text-xs"
                >
                  Ativar
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CreateVersionDialog({
  features,
  activeVersions,
  onCreated,
}: {
  features: FeatureInfo[];
  activeVersions: WeightsVersion[];
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState<string>("primary");
  const [notes, setNotes] = useState("");
  const [activateNow, setActivateNow] = useState(true);

  // Inicia com pesos da versão ativa correspondente (ou defaults se sem ativa).
  const startWeights = useMemo(() => {
    const current = activeVersions.find((v) => v.name === name);
    if (current) return current.weights;
    const out: Record<string, number> = {};
    features.forEach((f) => {
      out[f.weight_key] = f.default_value;
    });
    return out;
  }, [name, activeVersions, features]);

  const [weights, setWeights] = useState<Record<string, number>>(startWeights);

  // Resync quando muda o name
  useMemo(() => setWeights({ ...startWeights }), [startWeights]);

  const total = useMemo(
    () => Object.values(weights).reduce((s, v) => s + v, 0),
    [weights],
  );

  const submitMutation = useMutation({
    mutationFn: () =>
      createVersion({
        name,
        weights,
        notes: notes || undefined,
        activate_now: activateNow,
      }),
    onSuccess: () => {
      toast({ title: "Versão criada", description: activateNow ? "E ativada." : "Em rascunho." });
      onCreated();
    },
    onError: (err) =>
      toast({
        title: "Falha ao criar versão",
        description: formatApiError(err).detail,
        variant: "destructive",
      }),
  });

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Nova versão de pesos</DialogTitle>
        <DialogDescription>
          Ajuste os pesos por sinal. A soma não precisa bater 1: só a proporção
          relativa importa pra ordenação.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Lista</Label>
            <select
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="primary">Recomendados</option>
              <option value="exploratory">Outras opções</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <input
              id="activate-now"
              type="checkbox"
              checked={activateNow}
              onChange={(e) => setActivateNow(e.target.checked)}
            />
            <Label htmlFor="activate-now" className="text-xs">
              Ativar imediatamente
            </Label>
          </div>
        </div>

        <div>
          <Label className="text-xs">Anotações (opcional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: aumentei o valor pra priorizar candidatos dentro da faixa..."
            rows={2}
            className="mt-1"
          />
        </div>

        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
          {features.map((f) => (
            <div key={f.weight_key} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium">{prettyFeatureLabel(f.weight_key)}</span>
                <span className="text-muted-foreground">
                  {Math.round((weights[f.weight_key] ?? 0) * 100)}%
                </span>
              </div>
              <Slider
                value={[weights[f.weight_key] ?? 0]}
                onValueChange={([v]) =>
                  setWeights((prev) => ({ ...prev, [f.weight_key]: v }))
                }
                min={0}
                max={0.6}
                step={0.005}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
          <span>Soma das porcentagens: {Math.round(total * 100)}%</span>
          <span className="opacity-70">Pesos em 0 são ignorados.</span>
        </div>
      </div>

      <DialogFooter>
        <Button
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? "Salvando..." : "Criar versão"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * Transforma `category_weight` → "Categoria", `score_composto_weight` → "Score composto"
 * etc. Mantém a UI legível mesmo quando dev adicionar sinal novo (mapeia
 * automaticamente a partir do snake_case + remove "_weight"; user pode
 * editar override aqui pra labels mais bonitos caso queira).
 */
const PRETTY_OVERRIDE: Record<string, string> = {
  category_weight: "Categoria da agência",
  skills_weight: "Áreas de atuação",
  value_weight: "Pretensão salarial",
  experience_weight: "Experiência",
  domain_weight: "Domínio do projeto",
  agency_history_weight: "Histórico com a agência",
  score_composto_weight: "Qualidade comprovada",
  recency_weight: "Atualização do perfil",
  semantic_weight: "Similaridade semântica",
  approval_recency_weight: "Aprovações recentes",
};

function prettyFeatureLabel(key: string): string {
  if (PRETTY_OVERRIDE[key]) return PRETTY_OVERRIDE[key];
  return key
    .replace(/_weight$/, "")
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}
